import type { ApprovalRule } from "./messages";
import { FILE_EDIT_TOOLS, SHELL_TOOLS, assessRisk, touchesSecrets, type Risk } from "./risk";

/**
 * Approval rules: standing answers kept on the computer. They are deliberately narrow:
 *  - a deny always beats an allow;
 *  - an allow never applies to something `assessRisk` calls "careful", whatever the rule says;
 *  - an allow for a command only matches a single plain command: anything chained (; && || |), redirected, substituted or spread over lines is asked about;
 *  - an allow for a file only matches paths inside the rule's folder, never through `..`;
 *  - the match is on whole words: "git status" matches `git status -s` but not `git statusx`.
 */

/** What an agent asked for, reduced to the thing a rule can match. */
export interface RuleSubject {
  tool: string;
  /** Shell tools: the whole command. */
  command?: string;
  /** File tools: the path. */
  path?: string;
  /** Anything else: what the card shows. */
  text: string;
}

/** Anything that can join, redirect, substitute or continue a command onto another line. */
const CHAINED = /[;&|`<>\n\r]|\$\(|\$\{/;

const str = (v: unknown): string | undefined => (typeof v === "string" && v.length > 0 ? v : undefined);

/** From the agent's own request (the hook payload's `tool_input`), never from the text shown to the person. */
export function subjectFromPayload(tool: string, payload: unknown): RuleSubject {
  const input = ((payload as { tool_input?: unknown } | null)?.tool_input ?? {}) as Record<string, unknown>;
  if (SHELL_TOOLS.has(tool)) {
    const command = str(input.command) ?? "";
    return { tool, command, text: command };
  }
  if (FILE_EDIT_TOOLS.has(tool)) {
    const path = str(input.file_path) ?? str(input.notebook_path) ?? str(input.path) ?? "";
    return { tool, path, text: path };
  }
  const text = str(input.command) ?? str(input.file_path) ?? str(input.url) ?? str(input.query) ?? "";
  return { tool, text };
}

/** The same, from what an approval card carries (phone side: used to offer "Always allow"). */
export function subjectFromEvent(tool: string | undefined, detail: string | undefined): RuleSubject | null {
  if (!tool) return null;
  const first = (detail ?? "").split("\n")[0]!.trim();
  if (SHELL_TOOLS.has(tool)) return { tool, command: first, text: first };
  if (FILE_EDIT_TOOLS.has(tool)) return { tool, path: first, text: first };
  return { tool, text: first };
}

const sameFamily = (ruleTool: string, tool: string): boolean =>
  ruleTool === "*" || ruleTool === tool || (FILE_EDIT_TOOLS.has(ruleTool) && FILE_EDIT_TOOLS.has(tool));

const withSlash = (p: string): string => (p.endsWith("/") ? p : p + "/");
const hasDotDot = (p: string): boolean => p.split(/[\\/]/).includes("..");

/** Does `command` start with `prefix` as whole words? */
const startsWithWords = (command: string, prefix: string): boolean => command === prefix || command.startsWith(prefix + " ");

/** Every plain command in a chain (what a deny rule looks at, so `echo hi && rm -rf x` is still caught by "rm"). */
const segments = (command: string): string[] =>
  command
    .split(/&&|\|\||;|\||\n|(?<![>&])&(?!>)|\$\(|`/)
    .map((s) => s.trim().replace(/^(\w+=\S+\s+)+/, ""))
    .filter(Boolean);

export function ruleMatches(rule: ApprovalRule, subject: RuleSubject): boolean {
  if (!sameFamily(rule.tool, subject.tool)) return false;
  const prefix = rule.prefix.trim();
  if (subject.command !== undefined) {
    if (!prefix) return false; // "any command" is never a rule
    const command = subject.command.trim();
    if (rule.effect === "deny") return segments(command).some((s) => startsWithWords(s, prefix));
    return !CHAINED.test(command) && startsWithWords(command, prefix);
  }
  if (subject.path !== undefined) {
    const path = subject.path.trim();
    if (!path) return false;
    if (!prefix) return rule.effect === "deny" || (!hasDotDot(path) && !touchesSecrets(path));
    if (rule.effect === "allow" && hasDotDot(path)) return false;
    const folder = withSlash(prefix);
    return path === prefix || path.startsWith(folder);
  }
  return prefix === "" ? true : subject.text.startsWith(prefix);
}

export type RuleDecision = { effect: "allow" | "deny"; rule: ApprovalRule };

/** What the person's rules say about this request, or null (ask them). `risk` is `assessRisk` of the same request. */
export function decideByRules(rules: readonly ApprovalRule[], subject: RuleSubject, risk: Risk): RuleDecision | null {
  for (const rule of rules) if (rule.effect === "deny" && ruleMatches(rule, subject)) return { effect: "deny", rule };
  if (risk.level === "careful") return null;
  for (const rule of rules) if (rule.effect === "allow" && ruleMatches(rule, subject)) return { effect: "allow", rule };
  return null;
}

// Programs where the first word is not enough to say what will happen: the next word is part of the rule.
const WITH_SUBCOMMAND = new Set(["git", "npm", "pnpm", "yarn", "bun", "cargo", "go", "pip", "pip3", "uv", "poetry", "gradle", "mvn"]);
// Programs that are safe to allow by name alone.
const SAFE_ALONE = new Set(["ls", "cat", "head", "tail", "wc", "pwd", "grep", "rg", "tree", "which", "tsc", "eslint", "prettier", "pytest", "vitest", "jest", "ruff", "mypy"]);

export interface RuleOffer {
  tool: string;
  prefix: string;
  /** "npm test", "Edits in src/". */
  label: string;
}

const tailOf = (p: string): string => {
  const parts = p.split("/").filter(Boolean);
  return parts.slice(-2).join("/") + "/";
};

/**
 * The rule an approval could become with one tap ("Always allow ..."), or null when no safe rule fits (anything chained, anything
 * careful, programs that run arbitrary code, secrets). Never offers more than the person is looking at.
 */
export function ruleOffer(subject: RuleSubject | null, risk: Risk): RuleOffer | null {
  if (!subject || risk.level === "careful") return null;
  if (subject.command !== undefined) {
    const command = subject.command.trim();
    if (!command || CHAINED.test(command)) return null;
    const words = command.split(/\s+/);
    const first = words[0]!;
    if (!/^[\w./-]+$/.test(first)) return null;
    let n = 0;
    if (WITH_SUBCOMMAND.has(first) && words[1] && /^[a-z][\w:-]*$/.test(words[1])) {
      n = (first === "npm" || first === "pnpm" || first === "yarn" || first === "bun") && words[1] === "run" && words[2] && /^[a-z][\w:-]*$/.test(words[2]) ? 3 : 2;
    } else if (SAFE_ALONE.has(first)) n = 1;
    if (n === 0) return null;
    const prefix = words.slice(0, n).join(" ");
    return { tool: subject.tool, prefix, label: prefix };
  }
  if (subject.path !== undefined) {
    const path = subject.path.trim();
    if (!path.startsWith("/") || hasDotDot(path) || touchesSecrets(path)) return null;
    const dir = path.slice(0, path.lastIndexOf("/") + 1);
    if (dir.length < 2) return null;
    return { tool: subject.tool, prefix: dir, label: `Edits in ${tailOf(dir)}` };
  }
  return null;
}

/** Why a rule must not be saved, or null if it may. The computer checks this too: a phone is not trusted to keep the limits. */
export function ruleProblem(rule: Pick<ApprovalRule, "effect" | "tool" | "prefix">): string | null {
  const prefix = rule.prefix.trim();
  if (rule.tool === "*" && rule.effect === "allow") return "An allow rule must name one tool.";
  if (SHELL_TOOLS.has(rule.tool)) {
    if (!prefix) return "A rule for commands needs the start of the command.";
    if (rule.effect === "allow") {
      if (CHAINED.test(prefix)) return "An allow rule can't include ; && | or redirects.";
      if (assessRisk(rule.tool, prefix).level === "careful") return "Careful commands can't be allowed ahead of time.";
      const first = prefix.split(/\s+/)[0]!;
      if (!SAFE_ALONE.has(first) && prefix.split(/\s+/).length < 2) return "Allow a command with its subcommand, like “git status”.";
    }
  } else if (FILE_EDIT_TOOLS.has(rule.tool) && rule.effect === "allow") {
    if (!prefix.startsWith("/") || prefix.length < 3 || hasDotDot(prefix) || touchesSecrets(prefix)) return "Allow edits only inside a project folder.";
  }
  return null;
}
